import React, { Fragment } from 'react';
import { get, isEqual, pick, find, reduce, startCase, sumBy } from 'lodash';

import { arrayToObject, isEmpty, formatCurrency } from 'utils';
import { getVariantByOptions, isTrialItem } from 'utils/product';
import { truncatedText } from 'utils/collection';
import { calculateBundleItemValues } from 'shared/utils';

import IconPayment from 'components/icon/payment';
import Link from 'components/link';

const { API_PORT } = process.env;

export { calculateBundleItemValues };

export const ADDRESS_PROPS = Object.freeze([
  'name',
  'first_name',
  'last_name',
  'address1',
  'address2',
  'city',
  'state',
  'zip',
  'country',
  'phone',
  'company',
]);

export const COUNTRIES_WITH_TAX_ID = Object.freeze([
  'AE',
  'AR',
  'BR',
  'CL',
  'CN',
  'CO',
  'ID',
  'IN',
  'JP',
  'KR',
  'MX',
  'PE',
  'RU',
  'SA',
  'TR',
]);

export const EMPTY_ADDRESS = Object.freeze(
  ADDRESS_PROPS.reduce((acc, field) => {
    acc[field] = null;

    return acc;
  }, {}),
);

export const VARIANT_TABS = Object.freeze({
  OPTIONS: 'options',
  VARIANT: 'variant',
});

export function productHasOptions(product) {
  return (product?.options || []).length > 0;
}

export function itemHasVariants(product, editAddItem) {
  return (
    get(product, 'variants.results', []).length > 0 ||
    Boolean(editAddItem?.variant_id)
  );
}

/**
 * The ways an item can be purchased: a subscription item only recurs, and a
 * trial can only start from a cart or be edited on a submitted order
 */
export function getItemPurchaseChoices(
  product,
  { subscription, editing, isCart, editSubscriptionPlan } = {},
) {
  const { purchase_options, price, sale, sale_price, prices } = product || {};

  if (editSubscriptionPlan && get(purchase_options, 'subscription')) {
    const option = get(purchase_options, 'subscription', {});

    return [
      {
        ...option,
        plans: option.plans || [],
        type: 'subscription',
        name: option.name || 'Subscription',
      },
    ];
  }

  const options = subscription
    ? []
    : reduce(
        purchase_options,
        (acc, option, key) => {
          if (option.active) {
            switch (key) {
              case 'standard': {
                acc.push({
                  ...option,
                  type: 'standard',
                  name: option.name || 'One-time purchase',
                });

                break;
              }

              case 'subscription': {
                acc.push({
                  ...option,
                  plans: option.plans || [],
                  type: 'subscription',
                  name: option.name || 'Subscription',
                });

                break;
              }

              case 'trial': {
                if (isCart || editing) {
                  // trial product can only be added to the cart or edited in an already submitted order
                  acc.push({
                    ...option,
                    type: 'trial',
                    name: option.name || 'Try before you buy',
                  });
                }

                break;
              }

              default:
                break;
            }
          }

          return acc;
        },
        [],
      );

  // Default standard
  if (options.length === 0) {
    options.push({
      type: 'standard',
      price,
      sale,
      sale_price,
      prices,
    });
  }

  return options;
}

// Get payment due amount
export function paymentDue(order) {
  if (!order.canceled && order.payment_balance < 0) {
    return -order.payment_balance;
  }
}

// Get refund due amount
export function refundDue(order) {
  if (order.canceled) {
    return order.payment_total - order.refund_total;
  }
  if (order.payment_balance > 0) {
    return order.payment_balance;
  }
}

/**
 * Get payment status description
 *
 * @param {object} order
 */
export function paymentStatus(order) {
  if (refundDue(order)) {
    return 'Refund due';
  }

  if (
    order.payment_total > 0 &&
    order.payment_total > order.refund_total &&
    order.payment_total < order.grand_total &&
    paymentDue(order)
  ) {
    return 'Partially paid';
  }

  if (order.refund_total > 0) {
    if (order.refund_total < order.payment_total) {
      return 'Partially refunded';
    }

    if (order.refund_total === order.payment_total) {
      return 'Refunded';
    }
  }

  if (order.payment_total > 0 && order.payment_balance === 0) {
    return 'Paid';
  }

  if (order.grand_total > 0) {
    return 'Unpaid';
  }

  // If zero grand total and no payment was made
  return 'Free';
}

/**
 * Get fulfillment status description
 *
 * @param {object} order
 */
export function fulfillmentStatus(order) {
  if (order.canceled) {
    return 'Canceled';
  }

  if (order.hold || order.status === 'hold') {
    return 'On hold';
  }

  if (order.delivered) {
    if (order.item_quantity_returned > 0 || order.return_total > 0) {
      if (order.item_quantity_returned === order.item_quantity) {
        return 'Returned';
      }

      return 'Partially returned';
    }

    return 'Fulfilled';
  }

  if (order.item_quantity_delivered > 0) {
    return 'Partially fulfilled';
  }

  if (order.item_quantity_deliverable > 0) {
    return 'Unfulfilled';
  }

  return null;
}
/**
 * Checks if shipping.use_account dictates to use account address
 * @param {object} shipping - shipping object
 * @returns {boolean} - Returns true if account address should be used or false othwerwise
 */
export function shouldUseAccountShippingAddress(shipping) {
  if (!shipping) {
    return false;
  }

  // for legacy reasons undefined is equal to true
  if (shipping.use_account === true || shipping.use_account === undefined) {
    return true;
  }

  return false;
}

/**
 * Checks if billing.use_account dictates to use account address
 * @param {object} billing - billing object
 * @returns {boolean} - Returns true if account address should be used or false othwerwise
 */
export function shouldUseAccountBillingAddress(billing) {
  return billing?.use_account === true;
}

/**
 * Determine if objects contained the same address
 *
 * @param {object} left
 * @param {object} right
 * @returns {boolean}
 */
export function isSameAddress(left, right) {
  return isEqual(pick(left, ADDRESS_PROPS), pick(right, ADDRESS_PROPS));
}

/**
 * Get address fields from record
 *
 * @param {object} record
 * @returns {object}
 */
export function getAddressFromRecord(record = {}) {
  return pick(record, ADDRESS_PROPS);
}

/**
 * Get billing from record
 *
 * @param {object} record
 * @returns {object}
 */
export function getRecordBilling(record = {}) {
  const accountBilling = record.account?.billing ?? {};
  const recordBilling = record.billing ?? {};

  if (isEmpty(recordBilling)) {
    return accountBilling;
  }

  if (shouldUseAccountBillingAddress(recordBilling)) {
    return {
      ...recordBilling,
      ...accountBilling,
    };
  }

  return recordBilling;
}

// Get payment gateway name
export function paymentMethod(settings, billing = {}) {
  if (billing && !billing.method) {
    if (billing.card) {
      billing.method = 'card';
    } else if (billing.paypal) {
      billing.method = 'paypal';
    } else if (billing.amazon) {
      billing.method = 'amazon';
    } else if (billing.affirm) {
      billing.method = 'affirm';
    } else if (billing.resolve) {
      billing.method = 'resolve';
    } else if (billing.klarna) {
      billing.method = 'klarna';
    } else if (billing.ideal) {
      billing.method = 'ideal';
    } else if (billing.bancontact) {
      billing.method = 'bancontact';
    } else if (billing.google) {
      billing.method = 'google';
    } else if (billing.apple) {
      billing.method = 'apple';
    } else if (billing.twint) {
      billing.method = 'twint';
    } else if (billing.paysafecard) {
      billing.method = 'paysafecard';
    }
  }

  if (billing && billing.method) {
    const method = {
      ...(find(settings.payments.methods, { id: billing.method }) || {}),
    };

    if (billing.method === 'paypal') {
      method.name = 'PayPal';
    }

    if (method && billing.method === 'card') {
      const billingGateway = billing.card && billing.card.gateway;
      const gatewayId = billingGateway || method.gateway;
      const gateway = find(get(settings, 'payments.gateways'), {
        id: gatewayId,
      });
      if (gateway && gateway.name) {
        method.gateway = gateway;
      }
    }
    return method;
  }

  return undefined;
}
// Get shipping service name
export function getShippingServiceName(
  settings = {},
  record = {},
  serviceId = null,
) {
  const { shipping, shipment_rating } = record;
  const service = serviceId || shipping?.service;
  if (!service) {
    return undefined;
  }

  // prefer defined name
  if (!serviceId && shipping.service_name) {
    return shipping.service_name;
  }

  // find in shipment_rating
  const inRating = find(shipment_rating?.services || [], {
    id: service,
  });

  if (inRating) {
    return inRating.name || service;
  }

  // find in settings
  const inSettings = find(get(settings, 'shipments.services') || [], {
    id: service,
  });

  if (inSettings) {
    return inSettings.name || service;
  }

  // return id as name
  return service;
}

/**
 * Shipping services a merchant can pick before rating: the enabled ones,
 * without those of a disabled carrier
 *
 * @param {object} shipments settings
 * @returns {array} select options
 */
export function getShippingServiceOptions(shipments = {}) {
  const carriers = arrayToObject(shipments.carriers || []);
  return (shipments.services || [])
    .filter((service) => {
      const carrier = (service.carrier && carriers[service.carrier]) || {};
      return service.enabled && (!service.carrier || carrier.enabled);
    })
    .map((service) => ({
      value: service.id,
      label: service.name,
    }));
}

/**
 * Shipping services after rating: the configured ones with their rated
 * price, then custom services the rating added
 *
 * @param {object} record
 * @param {object} shipments settings
 * @param {array} serviceOptions from getShippingServiceOptions
 * @param {function} formatPrice
 * @returns {array} select options
 */
export function getRatedShippingServiceOptions(
  record = {},
  shipments = {},
  serviceOptions = [],
  formatPrice = formatCurrency,
) {
  const ratedOptions = (record.shipment_rating?.services || []).map(
    (service) => ({
      value: service.id,
      label: `${service.name || service.id} - ${formatPrice(
        service.price,
        record.currency,
      )}`,
    }),
  );
  const configured = serviceOptions.map((option) => {
    const rated = find(ratedOptions, { value: option.value });
    const service = find(shipments.services || [], { id: option.value });
    return {
      ...option,
      label: rated?.label || service?.name || option.label,
    };
  });
  const custom = ratedOptions.filter(
    (option) => option.value && !find(serviceOptions, { value: option.value }),
  );

  return [...configured, ...custom];
}

/**
 * Whether a record's shipping service is one of the listed ones ('rated')
 * or a name the merchant typed ('custom')
 *
 * @param {object} shipping
 * @param {array} serviceOptions
 * @param {array} ratedServiceOptions
 * @returns {string}
 */
export function getDefaultShippingType(
  shipping = {},
  serviceOptions = [],
  ratedServiceOptions = [],
) {
  if (
    shipping?.service &&
    !find(serviceOptions, { value: shipping.service }) &&
    !find(ratedServiceOptions, { value: shipping.service })
  ) {
    return 'custom';
  }

  return 'rated';
}

/**
 * Price of a service in a record's shipment rating
 *
 * @param {object} shipmentRating
 * @param {string} serviceId
 * @returns {number|undefined}
 */
export function getRatedServicePrice(shipmentRating, serviceId) {
  return find(shipmentRating?.services || [], { id: serviceId })?.price;
}

/**
 * Item discounts on a record, by source. Shipment discounts aren't included:
 * they come off the shipping price.
 *
 * @param {object} record
 * @returns {{ coupon: number, promotions: object }} promotions by id
 */
export function getOrderDiscountTotals(record = {}) {
  const totals = { coupon: 0, promotions: {} };

  (record.discounts || []).forEach((discount) => {
    if (discount.rule?.type === 'shipment') {
      return;
    }
    if (discount.id.startsWith('coupon')) {
      totals.coupon += discount.amount;
    } else if (discount.id.startsWith('promo')) {
      const promoId = discount.id.split('-')[1];
      totals.promotions[promoId] =
        (totals.promotions[promoId] || 0) + discount.amount;
    }
  });

  return totals;
}

// Get items with delivery type shipment
export function getShipmentItems(items = []) {
  return (items || []).reduce((result, item) => {
    if (item.bundle_items?.length > 0) {
      const bundleItemValues = calculateBundleItemValues(item);

      for (const bundleItem of item.bundle_items) {
        // skip items w/o shipment and canceled
        if (bundleItem.delivery === 'shipment' && item.quantity_total > 0) {
          result.push({
            ...bundleItem,
            id: item.id,
            bundle_item_id: bundleItem.id,
            quantity: item.quantity_total * bundleItem.quantity,
            price: bundleItemValues[bundleItem.id].price,
            discount_each: bundleItemValues[bundleItem.id].discount,
            tax_each: bundleItemValues[bundleItem.id].tax,
          });
        }
      }
    } else if (item.delivery === 'shipment') {
      result.push({ ...item });
    }

    return result;
  }, []);
}

export function getShippableItems(items = [], exItems) {
  const shippableItems = getShipmentItems(items);

  // Set quantities from shipment record if applicable
  if (exItems) {
    // Set quantities for shipped items
    for (const exItem of exItems) {
      const exShippableItem = shippableItems.find((item) => {
        if (item.id !== exItem.order_item_id) {
          return false;
        }
        if (item.bundle_item_id !== exItem.bundle_item_id) {
          return false;
        }
        return true;
      });

      if (exShippableItem) {
        exShippableItem.quantity_deliverable = exItem.quantity;
      } else {
        shippableItems.push({
          ...exItem,
          id: exItem.order_item_id,
          quantity_deliverable: exItem.quantity,
          order_item_id: undefined,
        });
      }
    }

    // Set all others to zero
    for (const shippableItem of shippableItems) {
      const exItem = exItems.find((item) => {
        if (item.order_item_id !== shippableItem.id) {
          return false;
        }
        if (item.bundle_item_id !== shippableItem.bundle_item_id) {
          return false;
        }
        return true;
      });

      if (!exItem) {
        shippableItem.quantity_deliverable = 0;
      }
    }
  }

  return shippableItems;
}

export function getReturnableItems(items = [], exItems) {
  const returnableItems = getShipmentItems(items);

  // Set quantities from return record if applicable
  if (exItems) {
    // Set quantities for returned items
    for (const exItem of exItems) {
      const exReturnableItem = find(returnableItems, (item) => {
        if (item.id !== exItem.order_item_id) {
          return false;
        }
        if (item.bundle_item_id !== exItem.bundle_item_id) {
          return false;
        }
        return true;
      });
      if (exReturnableItem) {
        exReturnableItem.quantity_returnable = exItem.quantity;
      } else {
        const orderItem = find(items, { id: exItem.order_item_id });
        const sourceItem = exItem.bundle_item_id
          ? find(orderItem?.bundle_items, { id: exItem.bundle_item_id })
          : orderItem;

        // Return items don't have quantity_consumed, take it from the order item.
        // Items that were restocked before had stock consumed as well
        const quantityConsumed =
          sourceItem?.quantity_consumed !== undefined
            ? sourceItem.quantity_consumed
            : exItem.quantity_restocked > 0
            ? exItem.quantity_restocked
            : undefined;

        returnableItems.push({
          ...exItem,
          id: exItem.order_item_id,
          quantity_returnable: exItem.quantity,
          quantity_consumed: quantityConsumed,
          order_item_id: undefined,
        });
      }
    }
  }

  return returnableItems.filter((item) => item.quantity_returnable > 0);
}

/**
 * Determines whether stock was reduced when the item was purchased.
 * Items without quantity_consumed are not restocked by the API.
 *
 * @param {object} item - Returnable item or bundle item
 * @returns {boolean}
 */
export function isItemStockConsumed(item) {
  return item?.quantity_consumed !== undefined;
}

/**
Returns an array of returned items with their corresponding quantity and restocked quantity.

* @param {Array} itemValues - An array of items with their corresponding quantity and restocked quantity.
* @param {Boolean} restock - A boolean value indicating whether or not restocking is required.
* @param {Array} returnableItems - An array of returnable items with their corresponding product, variant, and bundle item IDs.
* @returns {Array} An array of returned items with their corresponding quantity and restocked quantity.
*/
export function getReturnedItems(itemValues, restock, returnableItems) {
  const finalItems = itemValues
    .map((item, index) => {
      const { id, product_id, variant_id, bundle_item_id } =
        returnableItems[index];

      let quantityRestocked = 0;

      if (restock && isItemStockConsumed(returnableItems[index])) {
        quantityRestocked =
          typeof item.quantity_restocked === 'number'
            ? item.quantity_restocked
            : item.quantity || 0;
      }

      return {
        product_id,
        variant_id,
        bundle_item_id,
        order_item_id: id,
        quantity: item.quantity,
        quantity_restocked: quantityRestocked,

        // TODO: remove this when we have separated receiving from authorization
        quantity_received: item.quantity,
      };
    })
    .filter((i) => i.quantity > 0);
  return finalItems;
}

/**
 * The return form's values as the return record takes them: returned items
 * instead of the per-row quantities, without the restock switch.
 *
 * @param {object} values - { items: [{ quantity, quantity_restocked }], restock, ... }
 * @param {Array} returnableItems - getReturnableItems() rows the item values follow
 * @returns {object}
 */
export function getReturnFormValues(values, returnableItems) {
  return {
    ...values,
    restock: undefined,
    items: getReturnedItems(values.items, values.restock, returnableItems),
  };
}

/**
 * PUT body for an edited return: its items replace the stored ones.
 *
 * @param {object} values - getReturnFormValues() result
 * @returns {object}
 */
export function getReturnUpdateData(values) {
  return {
    ...values,
    items: undefined,
    $set: {
      items: values.items,
    },
  };
}

/**
 * What one unit of a returnable item credits: price less discount, plus tax
 * unless the order's prices include it.
 *
 * @param {object} item - Returnable item
 * @param {object} record - Order
 * @returns {{ price: number, discount: number, tax: number, total: number }}
 */
export function getReturnItemCreditAmounts(item, record) {
  const discount = item.discount_each || 0;
  const tax =
    record.tax_included || record.item_tax_included ? 0 : item.tax_each || 0;
  return {
    price: item.price,
    discount,
    tax,
    total: item.price - discount + tax,
  };
}

/**
 * Shipping tax that goes with a shipping credit, in proportion to the
 * order's shipping, when the order's shipping price doesn't include it.
 *
 * @param {object} record - Order
 * @param {number} shipmentTotal - Shipping credit
 * @returns {number}
 */
export function getReturnShipmentTaxPortion(record, shipmentTotal) {
  if (
    shipmentTotal <= 0 ||
    record.shipment_tax <= 0 ||
    record.shipment_tax_included ||
    record.shipment_total <= 0
  ) {
    return 0;
  }
  return (record.shipment_tax || 0) * (shipmentTotal / record.shipment_total);
}

/**
 * What a return credits, as the return form shows it before saving.
 *
 * @param {object} record - Order
 * @param {Array} returnableItems - getReturnableItems() rows
 * @param {Array} itemValues - [{ quantity }] per returnable item
 * @param {object} amounts - { shipmentTotal, restockFee }, 0 when empty
 * @returns {{ itemTotal: number, shippingTotal: number, total: number, maxRestockFee: number }}
 */
export function getReturnCreditPreview(
  record,
  returnableItems,
  itemValues,
  { shipmentTotal = 0, restockFee = 0 } = {},
) {
  const itemTotal = returnableItems.reduce(
    (acc, item, index) =>
      acc +
      getReturnItemCreditAmounts(item, record).total *
        (itemValues[index]?.quantity || 0),
    0,
  );
  const shippingTotal =
    shipmentTotal > 0
      ? shipmentTotal + getReturnShipmentTaxPortion(record, shipmentTotal)
      : 0;
  const hasItems = itemValues.some((item) => item.quantity > 0);
  return {
    itemTotal,
    shippingTotal,
    total: itemTotal + shippingTotal - restockFee,
    maxRestockFee: hasItems
      ? itemTotal
      : record.grand_total - record.shipment_total,
  };
}

/**
 * What a saved return credits: its items at the price the order charged,
 * plus shipping and extra credit, less the restocking fee.
 *
 * @param {object} retrn - Return
 * @param {object} record - Order with its items
 * @returns {{ total: number, itemTotal: number }}
 */
export function getReturnCreditTotals(retrn, record) {
  const { items, item_tax_included } = record;
  let itemTotal = 0;
  const total =
    (retrn.shipment_total || 0) +
    (retrn.shipment_tax || 0) +
    (retrn.extra_credit || 0) -
    (retrn.restock_fee || 0) +
    (retrn.items || []).reduce((acc, item) => {
      const orderItem = find(items, { id: item.order_item_id });
      let itemPrice = 0;
      if (!orderItem) {
        return acc;
      }
      if (item.bundle_item_id) {
        const bundleItemValues =
          calculateBundleItemValues(orderItem)[item.bundle_item_id];
        itemPrice +=
          (bundleItemValues.price -
            bundleItemValues.discount +
            (item_tax_included ? 0 : bundleItemValues.tax)) *
          item.quantity;
      } else {
        // return_*_total fields are aggregated across all returns of the
        // order item, so calculate the unit value and apply this return's quantity
        const quantityReturned = orderItem.quantity_returned || 0;
        const unitPrice =
          quantityReturned > 0
            ? (get(orderItem, 'return_price_total', 0) -
                get(orderItem, 'return_discount_total', 0) +
                (item_tax_included
                  ? 0
                  : get(orderItem, 'return_tax_total', 0))) /
              quantityReturned
            : 0;
        itemPrice += unitPrice * (item.quantity || 0);
      }
      itemTotal += itemPrice;
      return acc + itemPrice;
    }, 0);
  return { total, itemTotal };
}

/**
 * Shipment items from the shipment form's per-row quantities.
 *
 * @param {Array} itemValues - [{ quantity }] per shippable item
 * @param {Array} shippableItems - getShippableItems() rows
 * @returns {Array}
 */
export function getShipmentItemValues(itemValues, shippableItems) {
  return itemValues
    .map((item, index) => {
      const { id, product_id, variant_id, bundle_item_id } =
        shippableItems[index];
      return {
        product_id,
        variant_id,
        bundle_item_id,
        order_item_id: id,
        quantity: item.quantity,
      };
    })
    .filter((item) => item.quantity > 0);
}

/**
 * The shipment form's values as the shipment record takes them: shipped
 * items, the service and carrier from the shipping settings (a service's own
 * carrier wins), and whether to email the customer.
 *
 * @param {object} values - { items: [{ quantity }], service, carrier, $notify, ... }
 * @param {Array} shippableItems - getShippableItems() rows the item values follow
 * @param {object} shipmentSettings - settings.shipments
 * @returns {object}
 */
export function getShipmentFormValues(
  values,
  shippableItems,
  shipmentSettings,
) {
  const service = find(shipmentSettings?.services, { id: values.service });
  const carrier = find(shipmentSettings?.carriers, {
    id: (service && service.carrier) || values.carrier,
  });
  return {
    ...values,
    items: getShipmentItemValues(values.items, shippableItems),
    service: service ? service.id : null,
    service_name: values.service_name || (service && service.name),
    carrier: carrier ? carrier.id : null,
    carrier_name: values.carrier_name || (carrier && carrier.name),
    $notify: values.$notify ? true : false,
  };
}

/**
 * POST body for a new shipment: it goes to the order's shipping address and
 * emails the shipping confirmation when asked.
 *
 * @param {object} record - Order
 * @param {object} values - getShipmentFormValues() result
 * @returns {object}
 */
export function getShipmentCreateData(record, values) {
  const shipping = record.shipping || {};
  const accountName =
    (record.account && record.account.name) ||
    (record.billing && record.billing.name);
  return {
    ...values,
    destination: {
      name: shipping.name || accountName,
      address1: shipping.address1,
      address2: shipping.address2,
      city: shipping.city,
      state: shipping.state,
      zip: shipping.zip,
      country: shipping.country,
      phone: shipping.phone,
    },
    $notify: !values.$notify ? false : 'orders.shipped',
  };
}

/**
 * PUT body for an edited shipment: its items replace the stored ones, and
 * the customer gets the shipping update email when asked.
 *
 * @param {object} values - getShipmentFormValues() result
 * @returns {object}
 */
export function getShipmentUpdateData(values) {
  return {
    ...values,
    items: undefined,
    $set: {
      items: values.items,
    },
    $notify: !values.$notify ? false : 'orders.shipped-update',
  };
}

/**
 * Sum of a field over items.
 *
 * @param {Array} items
 * @param {string} field
 * @returns {number}
 */
export function getItemsQuantity(items = [], field = 'quantity') {
  return items.reduce((acc, item) => acc + (item[field] || 0), 0);
}

/**
 * Units of a delivery type ('shipment', 'giftcard', 'subscription') still to
 * be delivered.
 *
 * @param {string} delivery
 * @param {Array} items
 * @returns {number}
 */
export function getPendingDeliveryQuantity(delivery, items) {
  return items.reduce(
    (acc, item) =>
      acc +
      (item.delivery === delivery ||
      get(item, 'purchase_option.type') === delivery
        ? item.quantity_deliverable
        : 0),
    0,
  );
}

/**
 * Items (bundle items included, marked `bundleItem`) delivered as a type,
 * each with the record delivered for it (`<type>_delivered`).
 *
 * @param {string} type - 'giftcard' or 'subscription'
 * @param {Array} items
 * @param {object} itemsDelivered - { results } of gift cards or subscriptions
 * @returns {Array}
 */
export function getDeliveryItems(type, items, itemsDelivered = {}) {
  return (items || []).reduce(
    (acc, item) => [
      ...acc,
      ...(item.delivery === type || get(item, 'purchase_option.type') === type
        ? [
            {
              ...item,
              [`${type}_delivered`]: find(itemsDelivered.results, {
                order_item_id: item.id,
              }),
            },
          ]
        : []),
      ...(item.bundle_items
        ? getDeliveryItems(type, item.bundle_items, itemsDelivered).map(
            (bundleItem) => ({
              ...bundleItem,
              bundleItem: true,
            }),
          )
        : []),
    ],
    [],
  );
}

/**
 * What's left to fulfill on an order, by delivery type. `giftcardSendable`
 * counts gift cards that can be sent from the order (not inside bundles).
 *
 * @param {object} record - Order
 * @param {object} giftcardsDelivered - { results } of the order's gift cards
 * @param {object} subscriptionsDelivered - { results } of the order's subscriptions
 * @returns {{ shipment: number, giftcard: number, giftcardSendable: number, subscription: number, total: number }}
 */
export function getPendingFulfillment(
  record,
  giftcardsDelivered = {},
  subscriptionsDelivered = {},
) {
  const shipment = getPendingDeliveryQuantity(
    'shipment',
    getShippableItems(record.items),
  );
  const giftcardItems = getDeliveryItems(
    'giftcard',
    record.items,
    giftcardsDelivered,
  );
  const giftcard = getPendingDeliveryQuantity('giftcard', giftcardItems);
  const giftcardSendable = getPendingDeliveryQuantity(
    'giftcard',
    giftcardItems.filter((item) => !item.bundleItem),
  );
  const subscription = getPendingDeliveryQuantity(
    'subscription',
    getDeliveryItems('subscription', record.items, subscriptionsDelivered),
  );
  return {
    shipment,
    giftcard,
    giftcardSendable,
    subscription,
    total: shipment + giftcard + subscription,
  };
}

/**
 * Gift card lines that can still be sent from the order.
 *
 * @param {Array} items
 * @returns {Array}
 */
export function getSendableGiftcardItems(items = []) {
  return (items || []).filter(
    (item) => item.delivery === 'giftcard' && item.quantity_deliverable > 0,
  );
}

/**
 * Requests that send one gift card for an order line: the gift card (for the
 * line's price, emailed by the API) and the line's next delivered count.
 * Null when the line isn't on the order.
 *
 * @param {object} order - Order as stored, read just before sending
 * @param {object} values - { order_item_id, send_email, send_note }
 * @returns {{ giftcard: object, order: object }|null}
 */
export function getGiftcardFulfillment(order, values) {
  const item = find(order?.items, { id: values.order_item_id });
  if (!item) {
    return null;
  }
  return {
    giftcard: {
      ...values,
      order_id: order.id,
      order_item_id: item.id,
      amount: item.price,
      $notify: 'fulfillment',
    },
    order: {
      items: [
        {
          id: item.id,
          quantity_delivered: item.quantity_delivered + 1,
        },
      ],
    },
  };
}

// Determine if items are equivalent
export function isItemEqual(item1, item2) {
  for (let key of ['product_id', 'variant_id', 'options']) {
    if (item1[key] !== item2[key]) {
      return false;
    }
  }
  return true;
}

export function formatPaymentBrand(brand) {
  return startCase(brand);
}

// Render payment/card description with icon
export function renderPaymentSourceDetails(
  billing = {},
  specifiedMethod = null,
  short = false,
) {
  const method =
    specifiedMethod ||
    (billing.method ? billing.method : billing.card && 'card');

  switch (method) {
    case 'card': {
      const card = billing.card || {};
      const { brand, last4, exp_month, exp_year } = card;

      if (brand) {
        return (
          <Fragment>
            <IconPayment method="card" card={card} />
            &ensp;
            {short ? (
              <Fragment>{`${formatPaymentBrand(brand)} ${last4}`}</Fragment>
            ) : (
              <Fragment>
                {`${formatPaymentBrand(brand)} ending in ${last4}`}{' '}
                <span className="muted">
                  {`(expires ${exp_month}/${exp_year})`}
                </span>
              </Fragment>
            )}
          </Fragment>
        );
      }

      return 'No card on file';
    }

    case 'account':
      return 'Pay by invoice';

    default:
      return <span>&mdash;</span>;
  }
}

// Render payment/card description with icon
export function renderPaymentMethodDescription(
  settings,
  payment = {},
  maxlength = 0,
) {
  const { method, card, giftcard } = payment;
  switch (method) {
    case 'card':
      if (card) {
        const { brand, last4 } = card;
        if (brand) {
          return (
            <Fragment>
              <IconPayment method={method} card={card} />
              {truncatedText(
                `${formatPaymentBrand(brand)} ${last4}`,
                maxlength,
              )}
            </Fragment>
          );
        }
        return (
          <Fragment>
            <IconPayment method={method} card={card} />
            {truncatedText(`Credit card ${last4}`, maxlength)}
          </Fragment>
        );
      }
      return (
        <Fragment>
          <IconPayment method={method} />
          Credit card
        </Fragment>
      );
    case 'amazon':
    case 'paypal':
    case 'affirm':
    case 'resolve':
    case 'klarna':
    case 'bancontact':
    case 'google':
    case 'apple':
    case 'twint':
    case 'ideal':
    case 'paysafecard':
    case 'cash':
    case 'cash_on_delivery':
    case 'account': {
      const altMethod = paymentMethod(settings, payment);

      return (
        <Fragment>
          <IconPayment method={method} />
          {truncatedText(altMethod.name || method, maxlength)}
        </Fragment>
      );
    }

    case 'giftcard':
      return (
        <Fragment>
          <IconPayment method={method} />
          {truncatedText(
            giftcard ? `Gift card ${giftcard.code.substr(-4, 4)}` : 'Gift card',
            maxlength,
          )}
        </Fragment>
      );

    default: {
      const defaultMethod = paymentMethod(settings, payment);

      if (defaultMethod) {
        return (
          <Fragment>
            <IconPayment method={method} />
            {truncatedText(defaultMethod.name || method, maxlength)}
          </Fragment>
        );
      }

      return <Fragment>&mdash;</Fragment>;
    }
  }
}

export function orderPaymentMethodValid(billing = {}) {
  if (billing && billing.method) {
    switch (billing.method) {
      case 'card':
        return Boolean(billing.card || billing.account_card_id);

      default:
        return true;
    }
  }

  return false;
}

// Render shipment address description
export function renderShipmentAddressDescription(shipment = {}) {
  const {
    destination: { address1, address2, city, state, zip, country },
  } = shipment;
  return `
    ${address1}
    ${address2 ? ` ${address2}` : ''}${city ? `, ${city}` : ''}${
    state ? `, ${state}` : ''
  }${zip ? ` ${zip}` : ''}${country ? ` (${country})` : ''}
  `.trim();
}

// Render shipping weight description
export function renderShipmentWeightDescription(settings, weight) {
  const unit = settings.shipments.weight_unit;
  return `${weight} ${unit}`;
}

// Get an array of clean product options, removing the junk I guess
export function getCleanItemOptions(product, options, presetOptions) {
  if (!options) {
    return presetOptions;
  }

  const productOptions = arrayToObject(product.options);

  const existsOptions = new Set();
  const cleanOptions = [];

  if (Array.isArray(presetOptions)) {
    cleanOptions.push(...presetOptions);

    for (const { id } of presetOptions) {
      existsOptions.add(id);
    }
  }

  for (const option of options) {
    const productOption = option && productOptions[option.id];

    if (productOption && !existsOptions.has(productOption.id)) {
      const productOptionValues = arrayToObject(productOption.values);

      const productOptionValue =
        productOptionValues[option.value] ||
        find(productOptionValues, (optionValue) =>
          [optionValue.id, optionValue.name].includes(option.value),
        );

      cleanOptions.push({
        id: productOption.id,
        name: productOption.name,
        value: productOptionValue ? productOptionValue.name : option.value,
        value_id: productOptionValue ? productOptionValue.id : null,
        variant: productOption.variant,
      });
    }
  }

  return cleanOptions;
}

// remove excess fields to prevent unwanted overwrites on updates
export function getCleanBundleItems(bundleItems) {
  if (isEmpty(bundleItems)) {
    return undefined;
  }
  return bundleItems.map((item) => ({
    id: item.id,
    product: undefined,
    product_id: item.product_id,
    variant: undefined,
    variant_id: item.variant_id,
    options: item.options,
  }));
}

export function getItemPrice(item) {
  return item.price + (item.tax_each || 0) - (item.discount_each || 0);
}

export function getItemPriceTotal(item) {
  const quantity =
    (item.quantity || 0) -
    (item.quantity_returned || 0) -
    (item.quantity_canceled || 0);
  return quantity * getItemPrice(item);
}

export function getItemsPriceTotal(items = []) {
  return sumBy(items, getItemPriceTotal) || 0;
}

/**
 * @param {object} values
 * @param {object} options
 * @param {object} callbacks
 * @returns {Promise<any>}
 */
export async function prepareOrderItemFromModalData(
  values,
  options,
  callbacks,
) {
  const {
    addItemProduct,
    editAddItem,
    editAddIndex,
    showEditItems,
    editItems,
    getItemPrice,
  } = options;

  const itemProduct = editAddItem ? editAddItem.product : addItemProduct;

  const addItem = {
    ...values,
    quantity: values.quantity || 1,
    product: itemProduct,
    product_id: itemProduct.id,
    variant_id: values.variant_id || null,
    bundle_items: itemProduct.bundle_items,
    options: getCleanItemOptions(itemProduct, values.options),
  };

  if (values.bundle_options) {
    addItem.bundle_items = itemProduct.bundle_items.map((item) => ({
      ...item,
      options: getCleanItemOptions(
        item.product,
        values.bundle_options[item.product_id],
      ),
    }));
  }

  if (addItem.set_price === null) {
    callbacks.onLoading();

    const pricePayload = pick(addItem, [
      'id',
      'product_id',
      'quantity',
      'purchase_option',
    ]);

    const isVariantMode = !!addItem.variant_id;
    if (isVariantMode) {
      pricePayload.variant_id = addItem.variant_id;
    } else {
      pricePayload.options = addItem.options;
    }

    const pricedItem = await getItemPrice(pricePayload);

    if (pricedItem && !pricedItem.errors) {
      addItem.price = pricedItem.price;

      if (!showEditItems) {
        addItem.set_price = pricedItem.price;
      }
    }
  } else {
    addItem.price = addItem.set_price;
  }

  if (showEditItems) {
    if (
      addItem.purchase_option &&
      !addItem.purchase_option.type &&
      addItem.purchase_option.plan_id
    ) {
      const plan = addItem.product.purchase_options.subscription.plans.find(
        (plan) => plan.id === addItem.purchase_option.plan_id,
      );

      if (plan) {
        addItem.purchase_option = {
          ...addItem.purchase_option,
          type: 'subscription',
          billing_schedule: plan.billing_schedule,
        };
      }
    }

    if (!addItem.variant_id) {
      addItem.variant = getVariantByOptions(addItem.product, addItem.options);
    }

    const editItemsList = [...editItems];

    if (addItem.id) {
      const editIndex = editItemsList.findIndex(
        (item) => item.id === addItem.id,
      );
      editItemsList[editIndex] = { ...editItemsList[editIndex], ...addItem };
    } else if (editAddIndex !== null) {
      editItemsList[editAddIndex] = addItem;
    } else {
      editItemsList.push(addItem);
    }

    return callbacks.onEditItems(editItemsList);
  }

  return callbacks.onAddItem(addItem);
}

/**
 * A line in an edit of a record's items, or one to remove (`removed`)
 */
export function makeEditedOrderItem(item) {
  return {
    id: item.id,
    product: undefined,
    product_id: item.product ? item.product.id : item.product_id,
    variant: undefined,
    variant_id: item.variant ? item.variant.id : item.variant_id,
    price: item.set_price,
    quantity: item.quantity,
    options: item.options,
    removed: item.removed,
    bundle_items: getCleanBundleItems(item.bundle_items),
    purchase_option: item.purchase_option,
  };
}

export function makeOrderItem(item) {
  return {
    product_id: item.product_id,
    variant_id: item.variant_id,
    description: item.description,
    recurring: item.recurring,
    options: item.options,
    quantity: item.quantity,
    price: item.set_price,
    purchase_option: item.purchase_option,
    bundle_items: getCleanBundleItems(item.bundle_items),
    date_trial_end: item.date_trial_end,
  };
}

export function addTrialUpcomingPayments(record) {
  const { items = [], billing = {}, payment_balance } = record || {};
  const payments = (record.payments && record.payments.results) || [];
  const { method } = billing;
  const methodData = billing[method];

  if (!payments.length || !methodData) {
    return;
  }

  // Calculate the available balance to pay off upcoming payments.
  // given:
  //   * balance = payment_total - (grand_total - amount of unpaid trial items);
  //   * payment_balance = payment_total - grand_total, then
  // then:
  //   * balance = payment_balance + amount of unpaid trial items
  const unpaidTrialItems = items.reduce((acc, item, index) => {
    if (!item.paid && isTrialItem(item)) {
      acc.push({ ...item, index });
    }
    return acc;
  }, []);
  let balance =
    payment_balance +
    unpaidTrialItems.reduce((acc, item) => acc + getItemPriceTotal(item), 0);

  // get trial items for upcoming payments
  //
  // upcoming payments should be created for unpaid trial items that contain date_trial_end field
  // also need to sort the items to apply the available balance in the correct order
  const upcomingItems = unpaidTrialItems
    .filter((item) => Boolean(item.date_trial_end))
    .sort((a, b) => a.date_trial_end - b.date_trial_end);

  // create upcoming payments
  upcomingItems.forEach((item) => {
    if (item.paid === false && !item.payment_error) {
      // if the item is unpaid and there is no payment error,
      // it means that async payment is pending confirmation.
      return;
    }

    const itemPriceTotal = getItemPriceTotal(item);

    let amount;
    if (balance <= 0) {
      // No available balance. Full amount is must be paid.
      amount = itemPriceTotal;
    } else if (itemPriceTotal > balance) {
      // Balance partially covers the item price. Partial amount must be paid.
      amount = itemPriceTotal - balance;
      balance = 0;
    } else {
      // Balance fully covers the remaining amount. No payment required.
      balance -= itemPriceTotal;
      return;
    }

    // must be inserted at the beginning of the array
    // to display upcoming payments in the correct order
    record.payments.results.unshift({
      id: `upcoming-${item.id}`,
      item_id: item.id,
      item_index: item.index,
      upcoming: true,
      date_created: item.date_trial_end,
      amount,
      method,
      [method]: methodData,
      help: `${item.quantity} × ${item.product_name}`,
      error: item.payment_error,
    });
  });
}

// returns an object with arrays of items grouped by type
export function getItemGroups(items) {
  if (!items || !items.length) {
    return {};
  }

  // Copies each item with its index, instead of changing the record's items
  return items.reduce((acc, item, index) => {
    const indexedItem = { ...item, index };
    if (isTrialItem(item)) {
      acc.trial = acc.trial || [];
      acc.trial.push(indexedItem);
    } else {
      acc.standard = acc.standard || [];
      acc.standard.push(indexedItem);
    }
    return acc;
  }, {});
}

export function getCheckoutUrl(checkoutId, customUrl, testEnv = '') {
  if (!customUrl) {
    const testSegment = testEnv
      ? testEnv === 'test'
        ? 'test/'
        : `test-${testEnv}/`
      : '';
    return `${window.location.origin.replace(
      /:[0-9]+$/,
      `:${API_PORT}`,
    )}/checkout/${testSegment}${checkoutId}`;
  }

  return customUrl.replace('{checkout_id}', checkoutId);
}

export function makeAddressString(address = {}) {
  return [
    address.address1,
    address.address2,
    address.city,
    address.state,
    address.zip,
    address.country,
    address.tax_id ? `Tax ID: ${address.tax_id}` : '',
  ]
    .filter(Boolean)
    .join(', ');
}

export function shouldShowTaxID(name, _settings, country) {
  if (name !== 'shipping') {
    return false;
  }

  if (!country) {
    return false;
  }

  return COUNTRIES_WITH_TAX_ID.includes(country);
}

export function isExpired(month, year) {
  // Create a new date for the last day of the given expiry month and year
  const expiryDate = new Date(Date.UTC(year, month, 0));

  // Get the current date
  const currentDate = new Date();

  // Set the current date to UTC and to the end of the month
  currentDate.setUTCDate(0);
  currentDate.setUTCHours(23, 59, 59, 999);

  // Compare the two dates
  return currentDate > expiryDate;
}

export function renderShipmentPrice(shipment_price, shipment_total, currency) {
  // FREE shipping: render only FREE
  if (shipment_total <= 0) {
    return <span>FREE</span>;
  }

  // no discounts: render only price
  if (shipment_price === shipment_total) {
    return <span>{formatCurrency(shipment_total, currency)}</span>;
  }

  // render crossed price and discount
  return (
    <Fragment>
      <span className="crossed-text">
        {formatCurrency(shipment_price, currency)}
      </span>{' '}
      <span>{formatCurrency(shipment_total, currency)}</span>
    </Fragment>
  );
}

// A deleted product has no page to link to
export function renderProductName(item, name, className) {
  if (!item.product) {
    return <span>{name}</span>;
  }
  return (
    <Link to={`/products/${item.product_id}`} className={className}>
      {name}
    </Link>
  );
}
